import { Award, CoinStack, Conversation, Payout, Roulette, Smiley } from '@shared/icons';
import type { IconProps } from '@shared/icons/types';
import { twMerge } from '@shared/themes';
import { FRONTIFY_URL } from '@shared/utils';
import type { getTranslations } from 'next-intl/server';
import React, { type ComponentProps } from 'react';
import { ContentPod } from '../../../ContentPod/ContentPod';
import { ItemPod } from '../../../ItemPod/ItemPod';
import { ItemsGrid } from '../../../ItemsGrid/ItemsGrid';

type TranslationFunction = Awaited<ReturnType<typeof getTranslations>>;

type PlayLottolandProps = ComponentProps<'section'> & {
  t: TranslationFunction;
};

type IconComponent = React.ComponentType<IconProps>;

const PLAY_LOTTOLAND_ICONS: IconComponent[] = [CoinStack, Roulette, Award, Conversation];

const getPlayLottolandIcon = (index: number): IconComponent | null => {
  return PLAY_LOTTOLAND_ICONS[index] ?? null;
};

/**
 * A play lottoland section component that displays a title (h4)
 * and a list of play lottoland items in a responsive grid (2 columns on desktop, 1 on mobile).
 * The title and items are loaded from translations.
 *
 * @param t - Translation function from getTranslations('landing-pages.play-lottoland')
 * @param className - Additional CSS classes
 */
function PlayLottoland({ t, className, ...props }: PlayLottolandProps) {
  const items = (t.has('items') ? t.raw('items') : []) as Array<{ text: string }>;

  return (
    <section className={twMerge(className)} data-testid="play-lottoland-section" {...props}>
      <h4 className="heading-4 mb-4" data-testid="play-lottoland-title">
        {t('title')}
      </h4>
      <ItemsGrid
        items={items}
        renderItem={(item, index) => {
          const IconComponent = getPlayLottolandIcon(index);
          if (!IconComponent) return null;
          return (
            <ItemPod
              text={item.text}
              icon={<IconComponent size="regular" className="text-foreground" />}
              textClassName="paragraph-strong break-words min-w-0"
              className="border border-border bg-container-base rounded-m py-3 px-4 items-center"
            />
          );
        }}
        className="gap-4 pb-4"
        data-testid="play-lottoland-grid"
      />
      <ContentPod
        variant="image"
        imageUrl={`${FRONTIFY_URL}ywAiPLmSntC59vDhr8Gj.jpg`}
        title={t('content-pods.tapping-distance.title')}
        imageAlt={t('content-pods.tapping-distance.imageAlt')}
        description={t('content-pods.tapping-distance.description')}
      />
      <div className="flex flex-col md:flex-row gap-4 py-4">
        <div className="w-full md:w-1/2">
          <ContentPod icon={Smiley} title={t('content-pods.who-we-are.title')} description={t('content-pods.who-we-are.description')} />
        </div>
        <div className="w-full md:w-1/2">
          <ContentPod icon={Payout} title={t('content-pods.guaranteed-payouts.title')} description={t('content-pods.guaranteed-payouts.description')} />
        </div>
      </div>
    </section>
  );
}

export { PlayLottoland, type PlayLottolandProps };
